import styled from "styled-components";

import { colorsPalettes, rgba, setElevation, setFontVariant } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

export const MODAL_STYLES = {
    content: {
        width: "880px",
        padding: "0",
        overflow: "hidden",
    },
};

/** Shell for the two-column upload step: two equal halves plus the modal's own padding. */
export const UPLOAD_MODAL_STYLES = {
    content: {
        width: "880px",
        padding: "0",
    },
};

/**
 * Instructions on the left, drop zone on the right, split by a divider — deliberately duplicated
 * from the enrichment upload modal (`StyledLoadList.contact-enrichment`) so both modals read the
 * same.
 * That one lives in the deprecated `sub-modules/` layer and is wired to `EnrichmentType`,
 * the enrichment store and enrichment/suppression tracking, so it cannot be imported; extracting a
 * shared shell into `common-components/` is tracked in
 * {@link https://similarweb-rnd.atlassian.net/browse/SI-9129 SI-9129}.
 */
export const StyledUploadLayout = styled(Flex)`
    box-sizing: border-box;
    justify-content: space-between;
    width: 100%;
    padding: 24px 0;
`;

/** `min-width: 0` so the long instruction lines wrap inside the half rather than widening it. */
export const StyledInstructionsColumn = styled.div`
    box-sizing: border-box;
    flex: 1 1 0;
    min-width: 0;
    padding-right: 24px;
    border-right: 1px solid ${colorsPalettes.carbon[50]};
`;

export const StyledInstructionsList = styled.ul`
    padding: 0 0 0 10px;
    margin: 0 0 0 8px;
    list-style-type: disc;
`;

export const StyledInstructionItem = styled.li`
    ${setFontVariant("body1", colorsPalettes.carbon[400])}

    & + & {
        margin-top: 8px;
    }
`;

export const StyledExampleTable = styled.div<{ quantity: number }>`
    display: grid;
    grid-template-columns: repeat(${({ quantity }) => quantity}, auto);
    margin-top: 4px;
    border-top: 1px solid ${colorsPalettes.carbon[50]};
    border-left: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 3px;
    ${setElevation("Z1")}
    ${setFontVariant("caption", colorsPalettes.carbon[200])}
    line-height: 24px;
    background: ${colorsPalettes.carbon[0]};

    & > div {
        overflow: hidden;
        padding: 0 8px;
        white-space: nowrap;
        text-overflow: ellipsis;
        border-right: 1px solid ${colorsPalettes.carbon[50]};
        border-bottom: 1px solid ${colorsPalettes.carbon[50]};
    }

    & > div.header-cell {
        color: ${colorsPalettes.carbon[300]};
        background: ${colorsPalettes.carbon[25]};
    }
`;

export const StyledTemplateLink = styled.a`
    ${setFontVariant("subtitle2", colorsPalettes.blue[400])}
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

export const StyledResultRow = styled(Flex)<{ isError: boolean }>`
    box-sizing: border-box;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    background-color: ${({ isError }) =>
        isError ? rgba(colorsPalettes.red[400], 0.08) : colorsPalettes.bluegrey[100]};

    > svg {
        flex-shrink: 0;
    }
`;

/**
 * Claims the space between the icon and the trailing action so the action lands on the same edge in
 * every row — otherwise it follows the message and two stacked rows disagree on where it sits.
 */
export const StyledResultRowMessage = styled(Text)`
    flex: 1;
    min-width: 0;
`;

/** Footer with a caption on the left and the action buttons on the right. */
export const StyledSplitFooter = styled(Modal.Footer)`
    justify-content: space-between;
`;

/** Extra right padding keeps a long file name from sliding under the absolutely placed close button. */
export const StyledFileHeader = styled(Modal.Header)`
    padding-right: 48px;
`;

export const StyledConfirmModalContent = styled(Modal.Content)`
    box-sizing: border-box;
    max-height: none;
    overflow-x: hidden;
    overflow-y: visible;
    padding: 0 24px;
`;
